import { createContext, useCallback, useReducer } from 'react';
import { createReducer } from '../helpers';
import { FCWithChildren, ReducerActionWithPayload } from '../App.types';
import useAbortableEffect from '../Hooks/useAbortableEffect';
import { getAllClients, Currency, getAllCurrencies, ClientSimple } from '@round/api';

type OptionsState = { clients: ClientSimple[]; currencies: Currency[] };
type OptionsActions =
    | ReducerActionWithPayload<'setClients', ClientSimple[]>
    | ReducerActionWithPayload<'setCurrencies', Currency[]>;

const initialState: OptionsState = { clients: [], currencies: [] };

const reducer = createReducer<OptionsState, OptionsActions>({
    setClients: (state, { payload }) => ({
        ...state,
        clients: payload,
    }),
    setCurrencies: (state, { payload }) => ({
        ...state,
        currencies: payload,
    }),
});

export const OptionsContext = createContext<OptionsState | null>(null);
export const OptionsProvider: FCWithChildren = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const fetchClients = useCallback(async (requestInit?: RequestInit) => {
        try {
            const clients = await getAllClients(requestInit);
            dispatch({ type: 'setClients', payload: clients });
        } catch {
            // no-op
        }
    }, []);

    const fetchCurrencies = useCallback(async (requestInit?: RequestInit) => {
        try {
            const currencies = await getAllCurrencies(undefined, requestInit);
            dispatch({ type: 'setCurrencies', payload: currencies });
        } catch {
            // no-op
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            fetchClients({ signal });
            fetchCurrencies({ signal });
        },
        [fetchClients, fetchCurrencies]
    );

    return <OptionsContext.Provider value={state}>{children}</OptionsContext.Provider>;
};
